<template>
  <SamMenu
    class="sam-multi-select"
    :close-on-content-click="false"
    @open="onMenuOpen"
  >
    <template #activator="{ on, isMenuOpen, toggle }">
      <SamMultiChipInput
        :value="chipInput"
        :disabled="disabled"
        :placeholder="placeholder"
        :bablic-split="translateItemsSplit || false"
        @clear="clear"
        @remove-chip="remove"
        v-on="on"
      >
        <template #actions>
          <SamIcon
            :icon="isMenuOpen ? 'arrow-chevron-up' : 'arrow-chevron-down'"
            size="sm"
            @click.stop="toggle"
          />
        </template>
      </SamMultiChipInput>
    </template>
    <SamSelectList
      ref="selectList"
      v-model="input"
      :items="parsedItems"
      :title="listTitle"
      :has-select-all="hasSelectAll"
      :dont-translate-items="dontTranslateItems"
      multiple
      :debounce-time="debounceTime"
      :loading="loading"
      :filterable="filterable"
      :auto-hide-search="autoHideSearch"
      v-on="$listeners"
    >
      <template #no-data>
        <div
          v-if="!$slots['no-data'] && !loading"
          class="sam-multi-select__no-data"
        >
          No data to show
        </div>
        <template v-else-if="!loading">
          <slot name="no-data" />
        </template>
      </template>
    </SamSelectList>
  </SamMenu>
</template>

<script>
import SamMenu from '../SamMenu/SamMenu.vue';
import SamIcon from '../SamIcon/SamIcon.vue';
import SamMultiChipInput from '../SamMultiChipInput/SamMultiChipInput.vue';
import SamSelectList from '../SamSelectList/SamSelectList.vue';

export default {
  name: 'SamMultiSelect',
  components: {
    SamSelectList,
    SamMultiChipInput,
    SamMenu,
    SamIcon,
  },
  props: {
    /**
     * The items to be assigned to the dropdown list.
     * @type TextListItem[]
     */
    items: {
      type: Array,
      default: () => [],
      required: true,
    },
    /**
     * The component input value.
     * @type Array
     */
    value: {
      type: Array,
      default: () => [],
    },
    /**
     * If it is on disabled state
     * @values true, false
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * The input's placeholder
     * @type String
     */
    placeholder: {
      type: String,
      default: '',
      required: false,
    },
    /**
     * If you wish to split items into translatable segments
     */
    translateItemsSplit: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * If you wish to exclude items text from translation
     */
    dontTranslateItems: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Whether to set the list loading
     * @type Boolean
     */
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },

    /** SamSelectList props */
    /**
     * The list's title
     * @type String
     */
    listTitle: {
      type: String,
      default: undefined,
      required: false,
    },
    /**
     * With select all
     * @type Boolean
     */
    hasSelectAll: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * This value is used to specify debounce time used when search is set to search on input change
     * @type Number
     */
    debounceTime: {
      type: Number,
      required: false,
      default: 0,
    },
    /**
     * Whether the list should be filterable
     * @type Boolean
     */
    filterable: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Whether to hide search when items are less than 5
     * @type Boolean
     */
    autoHideSearch: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Whether to autofocus the search when the menu opens
     * @type Boolean
     */
    autoFocusSearch: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  computed: {
    input: {
      get() {
        return this.value ?? [];
      },
      set(value) {
        this.$emit('input', value);
        this.$emit('change', value);
      },
    },
    chipInput() {
      return (
        this.input?.map((val) => {
          return this.parsedItems.find((item) => item.value === val) || {};
        }) ?? []
      );
    },
    parsedItems() {
      return this.items ?? [];
    },
  },
  methods: {
    clear($event) {
      this.input = [];
      this.$emit('clear', $event);
    },
    remove(chip) {
      this.input = this.input.filter((item) => item !== chip.value);
      this.$emit('remove-chip', chip);
    },
    onMenuOpen() {
      if (this.autoFocusSearch && this.$refs.selectList) {
        this.$refs.selectList.focusInput();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.sam-multi-select::v-deep {
  width: 100%;
  display: block;

  .sam-multi-select__no-data {
    @extend .text--caption, .p-4;
    color: $neutral--60;
    font-style: italic;
    text-align: center;
  }

  .menu__content {
    @extend .pb-0;
  }
}
</style>
